import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

//#region Material Components
import { withStyles } from '@material-ui/styles';
import MuiTable from '@material-ui/core/Table';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiIconButton from '@material-ui/core/IconButton';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import MoreIcon from '@material-ui/icons/MoreVert';
import WhatshotIcon from '@material-ui/icons/Whatshot';
//#endregion

//#region Internal
import { Components } from './../../assets/Dimensions';
import { FB, fmtNum, fmtDate, urgencyTier } from './../../assets/feedbackTheme';
//#endregion

// ─── Why this exists ─────────────────────────────────────────────────────────
// FeedBacks-only table. "Tüm Zamanlar" returns ~45k rows, so we window them (only
// the visible slice is mounted; off-screen space is reserved by two spacer rows).
// On top of the windowing it carries the redesign: teal header, curated columns
// (driven by columnDefs from the page), formatted cells, urgency (flame + red) on
// high report counts, and the non-destructive "done" state (green-lit rows that
// stay). Search + "hide done" filtering happen here so windowing runs on the
// filtered list. Windowing is exact only when every body row is ROW_HEIGHT tall.

//#region Constants
const ROW_HEIGHT = 44;
const OVERSCAN = 8;
const ACTION_COL_WIDTH = 64;
const COL_MIN_WIDTH = 150;
const DEFAULT_VIEWPORT = 600;
//#endregion

//#region Styled
const Table = withStyles({
  root: { backgroundColor: FB.surface, borderCollapse: 'separate', tableLayout: 'fixed' },
})(MuiTable);

const TableCell = withStyles({
  root: { padding: '6px 12px', borderBottom: `1px solid ${FB.border}` },
})(MuiTableCell);

const IconButton = withStyles({
  label: { width: Components.Image.Huge.Width, height: Components.Image.Huge.Height },
})(MuiIconButton);
//#endregion

//#region Inline style
const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const headerCell = {
  ...ellipsis,
  position: 'sticky',
  top: 0,
  zIndex: 5,
  backgroundColor: FB.accent,
  color: '#fff',
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: 0.2,
  borderBottom: 'none',
};

const spacerCell = { padding: 0, border: 0 };

const badge = (bg, color, border) => ({
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 500,
  padding: '2px 9px',
  borderRadius: 999,
  backgroundColor: bg,
  color,
  border: `1px solid ${border}`,
});
//#endregion

const renderStatus = (value, isDone) => {
  if (isDone) return <span style={badge(FB.doneSoft, FB.done, FB.doneBorder)}>Tamamlandı</span>;
  const v = Number(value);
  if (v === 0) return <span style={badge(FB.openSoft, FB.open, FB.openBorder)}>Açık</span>;
  if (v === 1) return <span style={badge(FB.doneSoft, FB.done, FB.doneBorder)}>Çözülen</span>;
  return <span style={badge(FB.openSoft, FB.open, FB.openBorder)}>{String(value)}</span>;
};

const renderCellValue = (type, value, isDone) => {
  if (type === 'status') return renderStatus(value, isDone);
  if (value === null || value === undefined || value === '') {
    return type === 'count' ? fmtNum(0) : <span style={{ color: FB.textMuted }}>—</span>;
  }
  switch (type) {
    case 'count': {
      const tier = urgencyTier(value);
      if (tier === 'high') {
        return (
          <span style={{ color: FB.danger, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <WhatshotIcon style={{ fontSize: 15 }} />
            {fmtNum(value)}
          </span>
        );
      }
      if (tier === 'mid') return <span style={{ color: FB.open, fontWeight: 600 }}>{fmtNum(value)}</span>;
      return <span style={{ fontWeight: 500 }}>{fmtNum(value)}</span>;
    }
    case 'date':
      return <span style={{ color: FB.textSecondary }}>{fmtDate(value)}</span>;
    case 'answer':
      return <span style={badge(FB.accentSoft, FB.accent, FB.accentBorder)}>{String(value)}</span>;
    default:
      return String(value);
  }
};

// "done" identity matches markAsDone (content index 3 = BookSectionCropId).
const doneKeyOf = (row) => {
  const c = row.data && row.data.content;
  return c && c[3] ? c[3].content : row.data && row.data.id;
};

const VirtualizedGrid = ({
  columnDefs,
  rows,
  options,
  selectedRowId,
  rowClick,
  doneIds,
  hideDone,
  searchText,
}) => {
  const defs = columnDefs || [];
  const allRows = rows || [];
  const hasActions = typeof options === 'function';
  const colCount = defs.length + (hasActions ? 1 : 0);
  const countContentIdx = useMemo(() => {
    const d = defs.find((x) => x.type === 'count');
    return d ? d.index : -1;
  }, [defs]);

  const filtered = useMemo(() => {
    const q = (searchText || '').trim().toLowerCase();
    if (!q && !hideDone) return allRows;
    return allRows.filter((r) => {
      if (hideDone && doneIds && doneIds.has(doneKeyOf(r))) return false;
      if (q) {
        const hit = defs.some((d) => {
          const cell = r.data.content[d.index];
          return cell && String(cell.content == null ? '' : cell.content).toLowerCase().includes(q);
        });
        if (!hit) return false;
      }
      return true;
    });
  }, [allRows, searchText, hideDone, doneIds, defs]);

  const scrollRef = useRef(null);
  const rafRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(DEFAULT_VIEWPORT);

  useLayoutEffect(() => {
    const measure = () => {
      if (scrollRef.current) setViewportHeight(scrollRef.current.clientHeight);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [filtered]);

  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    []
  );

  const onScroll = () => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (scrollRef.current) setScrollTop(scrollRef.current.scrollTop);
    });
  };

  const total = filtered.length;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const visibleCount = Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN * 2;
  const endIndex = Math.min(total, startIndex + visibleCount);
  const topPad = startIndex * ROW_HEIGHT;
  const bottomPad = Math.max(0, (total - endIndex) * ROW_HEIGHT);
  const visibleRows = filtered.slice(startIndex, endIndex);

  const tableMinWidth = ACTION_COL_WIDTH + defs.length * COL_MIN_WIDTH;
  const emptyMsg = allRows.length === 0 ? 'Veri bulunamadı.' : 'Aramayla eşleşen kayıt yok.';

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      style={{
        height: 'calc(100vh - 320px)',
        minHeight: 300,
        overflow: 'auto',
        border: `1px solid ${FB.border}`,
        borderRadius: 12,
        backgroundColor: FB.surface,
      }}
    >
      <Table style={{ width: '100%', minWidth: tableMinWidth }}>
        <TableHead>
          <MuiTableRow>
            {hasActions && <TableCell style={{ ...headerCell, width: ACTION_COL_WIDTH }} />}
            {defs.map((d, headIndex) => (
              <TableCell
                key={headIndex}
                title={d.label}
                style={{ ...headerCell, width: COL_MIN_WIDTH }}
              >
                {d.label}
              </TableCell>
            ))}
          </MuiTableRow>
        </TableHead>

        <TableBody>
          {total === 0 ? (
            <MuiTableRow>
              <TableCell
                colSpan={colCount || 1}
                style={{ textAlign: 'center', padding: 40, color: FB.textMuted }}
              >
                {emptyMsg}
              </TableCell>
            </MuiTableRow>
          ) : (
            <>
              {topPad > 0 && (
                <tr style={{ height: topPad }}>
                  <td colSpan={colCount} style={spacerCell} />
                </tr>
              )}

              {visibleRows.map((row, i) => {
                const realIndex = startIndex + i;
                const selected = selectedRowId != null && selectedRowId === realIndex;
                const isDone = doneIds && doneIds.has(doneKeyOf(row));
                const countCell =
                  countContentIdx >= 0 ? row.data.content[countContentIdx] : null;
                const urgent = !isDone && countCell && urgencyTier(countCell.content) === 'high';
                const bg = isDone
                  ? FB.doneSoft
                  : selected
                  ? FB.accentSoft
                  : realIndex % 2
                  ? FB.surfaceAlt
                  : FB.surface;
                return (
                  <MuiTableRow
                    key={realIndex}
                    id={row.data.id}
                    onClick={(e) => rowClick && rowClick(e)}
                    onMouseEnter={(e) => {
                      if (!isDone) e.currentTarget.style.backgroundColor = '#eef6f3';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = bg;
                    }}
                    style={{ height: ROW_HEIGHT, backgroundColor: bg }}
                  >
                    {hasActions && (
                      <TableCell
                        style={{
                          width: ACTION_COL_WIDTH,
                          boxShadow: urgent ? `inset 3px 0 0 0 ${FB.danger}` : 'none',
                        }}
                      >
                        <IconButton
                          id={row.data.id}
                          style={{ padding: 0, color: isDone ? FB.done : FB.accent }}
                          onClick={(e) => {
                            e.stopPropagation();
                            options(row.data, realIndex);
                          }}
                        >
                          <MoreIcon
                            style={{
                              width: Components.Image.Big.Width,
                              height: Components.Image.Big.Height,
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    )}

                    {defs.map((d, cellIndex) => {
                      const cell = row.data.content[d.index];
                      const value = cell ? cell.content : null;
                      return (
                        <TableCell
                          key={cellIndex}
                          title={value == null ? '' : String(value)}
                          style={{
                            ...ellipsis,
                            width: COL_MIN_WIDTH,
                            fontSize: 13,
                            color: FB.text,
                            opacity: isDone ? 0.6 : 1,
                          }}
                        >
                          {renderCellValue(d.type, value, isDone)}
                        </TableCell>
                      );
                    })}
                  </MuiTableRow>
                );
              })}

              {bottomPad > 0 && (
                <tr style={{ height: bottomPad }}>
                  <td colSpan={colCount} style={spacerCell} />
                </tr>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default VirtualizedGrid;
